import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {GoogleService} from '../api';
import type {Location, PlaceIdDetail} from '../api/types';
import {addLocation} from '../store';
import {getLocationList, getUserLocation} from '../store/location/selectors';
import {MyWeather} from '../utils';
import useApiClient, {ApiError, isThrottled} from './useApiClient';

export type MyWeatherPlace =
  | {status: 'loading'; place: null}
  | {status: 'ready'; place: Location}
  | {status: 'missing'; place: null}
  /**
   * The lookup failed otherwise (offline, a server error, or `throttled`:
   * refused for too many requests, FE #427): ask again.
   */
  | {status: 'error'; place: null; throttled: boolean; retry: () => void};

const LOADING: MyWeatherPlace = {status: 'loading', place: null};
const MISSING: MyWeatherPlace = {status: 'missing', place: null};

/**
 * The place a page shows by its id (FE #381 W4, the webapp's
 * `/app/places/:id`): the device's own when it holds it (a favourite or a
 * recent one), else, for a Google place id, built from its details
 * (`MyWeather.placeFromDetail`) and kept as a recent place, so a shared link
 * works. A point the device does not hold cannot be rebuilt (it carries no
 * timezone): missing, as is an id Google does not know. The lookup is
 * silent: the page says "not found" itself. The GPS entry answers to its
 * own hash (`MyWeather.USER_LOCATION_HASH`): the current location's page.
 */
const useMyWeatherPlace = (hash: string): MyWeatherPlace => {
  const dispatch = useDispatch();
  const {fetch} = useApiClient();
  const gps = hash === MyWeather.USER_LOCATION_HASH;
  const userLocation = useSelector(getUserLocation);
  const listed = useSelector(getLocationList).find((p) => p.hash === hash);
  const held = gps ? userLocation : listed;
  const [lookup, setLookup] = React.useState<{
    hash: string;
    failure: 'missing' | 'error' | 'throttled';
  } | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  const askGoogle = !held && !gps && !MyWeather.isPointHash(hash);
  React.useEffect(() => {
    if (!askGoogle) {
      return undefined;
    }
    let current = true;
    fetch(GoogleService.placeId(hash), {silent: true})
      .then((detail: PlaceIdDetail) => {
        if (!current) {
          return;
        }
        // No timezone, no forecast to show: as good as unknown.
        if (!detail.timezone) {
          setLookup({hash, failure: 'missing'});
          return;
        }
        dispatch(addLocation(MyWeather.placeFromDetail(hash, detail)));
      })
      .catch((error: unknown) => {
        if (current) {
          // Only the server saying no (4xx) means there is no such place;
          // a 429 says nothing of the place (FE #427).
          const status =
            error instanceof ApiError ? error.statusCode : undefined;
          const missing = status !== undefined && status >= 400 && status < 500;
          setLookup({
            hash,
            failure: isThrottled(error)
              ? 'throttled'
              : missing
                ? 'missing'
                : 'error',
          });
        }
      });
    return () => {
      current = false;
    };
  }, [askGoogle, dispatch, fetch, hash, attempt]);

  const retry = React.useCallback(() => {
    setLookup(null);
    setAttempt((n) => n + 1);
  }, []);

  if (held) {
    return {status: 'ready', place: held};
  }
  if (!askGoogle) {
    return MISSING;
  }
  if (lookup?.hash === hash) {
    return lookup.failure === 'missing'
      ? MISSING
      : {
          status: 'error',
          place: null,
          throttled: lookup.failure === 'throttled',
          retry,
        };
  }
  return LOADING;
};

export default useMyWeatherPlace;
